import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { z } from 'zod'

/**
 * The `/dhb` subpages, in the order they appear in the
 * tab bar. Peer views over the same shared context rather than distinct
 * destinations, so they live on one route and are selected by the `tab` search
 * param.
 *
 * The values are a public URL contract — they end up in bookmarks and shared
 * links — so every tab is named for what it contains.
 *
 * What each tab is *called* and what it *renders* lives in the page's registry
 * (`pages/dhb/tabs/Registry`), not here.
 */
export enum DhbTab {
  Imports = 'imports',
  Import = 'import',
  Genealogy = 'genealogy',
  ApiPlayground = 'api-playground',
  DocumentViewer = 'document-viewer',
  ExtractionReview = 'extraction-review',
  Matching = 'matching',
}

/** Every tab id, in tab-bar order — the enum's declaration order. */
export const DHB_TAB_IDS: readonly DhbTab[] = Object.values(DhbTab)

export const DEFAULT_DHB_TAB: DhbTab = DhbTab.Import

/**
 * The six stages of the DHB (Data History Book) import wizard. Persisted in the
 * URL so the step survives refreshes / deep links.
 */
export const DHB_IMPORT_STEPS = [
  'import',
  'processing',
  'processed',
  'quality-review',
  'priority-decisions',
  'summary',
] as const

export type DhbImportStep = (typeof DHB_IMPORT_STEPS)[number]

export const dhbSearchParams = z.object({
  [SEARCH_PARAM_ORG_ID]: z.string().optional(),
  [SEARCH_PARAM_SITE_ID]: z.string().optional(),
  tab: z.enum(DhbTab).optional(),
  /** Import wizard stage — only meaningful while `tab === 'import'`. */
  step: z.enum(DHB_IMPORT_STEPS).optional(),
  /** Active backend import, retained in the URL so polling resumes after refresh. */
  importId: z.string().optional(),
  /** Matching report drill-down — the selected report extraction key. */
  matchingReportKey: z
    .string()
    .regex(/^[a-fA-F0-9]{64}$/)
    .optional(),
})

export type DhbSearch = z.infer<typeof dhbSearchParams>
